/**
 * A hub's own address (the Navigation review, slice (c): /app/catalog): it opens the tab the person last used there,
 * where they still see it, else the hub's first tab they see, keeping the address's query. A hub of which they see no
 * tab sends them to /app, which lands them as it always does. It waits for the org's packs at most the landing's wait
 * (hubIndexTarget).
 */
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hubIndexTarget, hubTabs, type Hub } from "@/lib/nav/hubs";
import { lastHubTab } from "@/lib/nav/hub-memory";
import { LANDING_WAIT_MS } from "@/lib/nav/landing";
import { useAppMenu } from "@/lib/nav/use-app-menu";

export function HubIndex({ hub }: { hub: Hub }) {
  const { context, landing } = useAppMenu();
  const { search } = useLocation();
  const person = useAuth().user?.id ?? "";
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setTimedOut(true), LANDING_WAIT_MS);
    return () => window.clearTimeout(t);
  }, []);
  const to = hubIndexTarget({
    packsKnown: landing.packsKnown,
    timedOut,
    tabs: hubTabs(hub, context),
    remembered: lastHubTab(person, hub.id),
    search,
  });
  if (!to) {
    return (
      <div role="status" className="flex h-full items-center justify-center gap-2 p-8 text-sm text-on-surface-muted">
        <Loader2 aria-hidden className="size-4 animate-spin" />
        Opening {hub.label}
      </div>
    );
  }
  return <Navigate to={to} replace />;
}
